import { type FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';

import dibsCss from 'dibs-css';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';

// components
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpRespSidebarDetailsAbout } from './PdpRespSidebarDetailsAbout.tsx';
import { PdpRespSidebarDetailsCustomization } from './PdpRespSidebarDetailsCustomization.tsx';

import { useShowPdpEstimateInTab } from '../hooks/usePdpEstimateSimilarSold.ts';

// types
import { type PdpRespSidebarDetails_viewer$key as Viewer } from './__generated__/PdpRespSidebarDetails_viewer.graphql.ts';
import { type PdpRespSidebarDetails_item$key as Item } from './__generated__/PdpRespSidebarDetails_item.graphql.ts';
import { type PdpRespSidebarDetails_user$key as User } from './__generated__/PdpRespSidebarDetails_user.graphql.ts';

const PdpRespSidebarDetailsPricingGuidance = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSidebarDetailsPricingGuidance" */ './PdpRespSidebarDetailsPricingGuidance.tsx'
        )
);

const PdpSharedEstimateSimilarSoldPricing = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedEstimateSimilarSoldPricing" */ '../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldPricing.tsx'
        )
);

const viewerFragment = graphql`
    fragment PdpRespSidebarDetails_viewer on Viewer {
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...usePdpEstimateSimilarSold_viewer @include(if: $afterDisplay)
        }
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarDetails_item on Item {
        ...PdpRespSidebarDetailsAbout_item
        ...PdpRespSidebarDetailsCustomization_item
        ...PdpRespSidebarDetailsPricingGuidance_item @include(if: $afterDisplay)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...PdpSharedEstimateSimilarSoldPricing_item @include(if: $afterDisplay)
            ...usePdpEstimateSimilarSold_item @include(if: $afterDisplay)
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarDetails_user on User {
        ...PdpRespSidebarDetailsAbout_user
        ...PdpRespSidebarDetailsCustomization_user
    }
`;

export const PdpRespSidebarDetails: FC<{
    viewer: Viewer;
    item: Item;
    user: User | null;
    skuId: string | null;
    itemQuantity: number | null;
}> = ({ viewer: viewerRef, item: itemRef, user: userRef, skuId }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef) || null;
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});

    const { currency } = useCurrency();
    const { isPdpEstimateSimilarSoldTestVariant } = useServerVarsContext();
    const showPdpEstimateInTab = useShowPdpEstimateInTab({
        viewer: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? viewer : null,
        item: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? item : null,
        currency,
    });

    return (
        <>
            <PdpRespSidebarDetailsAbout item={item} user={user} skuId={skuId} />
            <PdpRespSidebarDetailsCustomization item={item} user={user} />

            {afterDisplay && isPdpEstimateSimilarSoldTestVariant && showPdpEstimateInTab && (
                <Suspense fallback="">
                    <div className={dibsCss.mtSmall}>
                        <PdpSharedEstimateSimilarSoldPricing
                            currency={currency}
                            item={item}
                            titleClassName={dibsCss.sassyFontBodyMediumHeavy}
                        />
                    </div>
                </Suspense>
            )}

            {afterDisplay && !isPdpEstimateSimilarSoldTestVariant && (
                <Suspense fallback={null}>
                    <PdpRespSidebarDetailsPricingGuidance item={item} skuId={skuId} />
                </Suspense>
            )}
        </>
    );
};
